import {
  DASHBOARD_DATE_SELECTION_KIND,
  DASHBOARD_PERIOD_LABELS,
  DASHBOARD_PERIODS,
  dashboardAvailableDayRange,
  dashboardSelectionLabel,
  type DashboardDateSelection,
  type DashboardPeriod,
  utcDay,
} from "@nova-ai/shared"
import { CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react"
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react"
import { createPortal } from "react-dom"
import type { DateRange } from "react-day-picker"

import { cn } from "../../lib/utils"
import { Calendar } from "../ui/calendar"

type DashboardDatePickerProps = {
  value: DashboardDateSelection
  onChange: (next: DashboardDateSelection) => void
  /** UTC day override for deterministic tests. */
  today?: string
  /** Optional bounded evidence window for surfaces that reuse this interaction
   * but honestly expose less history than the main dashboard. */
  availableRange?: { from: string; to: string }
  periods?: readonly DashboardPeriod[]
  label?: string
}

type PickerMode = DashboardDateSelection["kind"]
type FloatingPosition = {
  left: number
  top: number
  width: number
  maxHeight: number
  placement: "top" | "bottom"
}

const PANEL_GUTTER = 8
const PANEL_WIDTH = 372
const PANEL_GAP = 6
const MONTH_LABELS = Array.from({ length: 12 }, (_, monthIndex) =>
  new Intl.DateTimeFormat("en-GB", { month: "short", timeZone: "UTC" }).format(
    new Date(Date.UTC(2026, monthIndex, 1))
  )
)

export function DashboardDatePicker({
  value,
  onChange,
  today,
  availableRange,
  periods = DASHBOARD_PERIODS,
  label = "Dashboard date range",
}: DashboardDatePickerProps) {
  const currentDay = today ?? availableRange?.to ?? utcDay(Date.now())
  const isBoundedRange = availableRange !== undefined
  const currentDate = dayToLocalDate(currentDay)
  const available =
    availableRange ?? dashboardAvailableDayRange(new Date(`${currentDay}T12:00:00.000Z`))
  const earliestDate = dayToLocalDate(available.from)
  const earliestMonthDate = availableRange
    ? earliestDate
    : new Date(currentDate.getFullYear(), currentDate.getMonth() - 11, 1, 12)
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<PickerMode>(value.kind)
  const [year, setYear] = useState(currentDate.getFullYear())
  const [draft, setDraft] = useState<DateRange | undefined>(() => selectionToRange(value))
  const [position, setPosition] = useState<FloatingPosition | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const valueRef = useRef(value)
  const pickerId = useId()

  useEffect(() => {
    valueRef.current = value
  }, [value])

  const close = useCallback(({ returnFocus }: { returnFocus: boolean }) => {
    setOpen(false)
    setDraft(selectionToRange(valueRef.current))
    if (returnFocus) {
      requestAnimationFrame(() => triggerRef.current?.focus())
    }
  }, [])

  function commit(next: DashboardDateSelection) {
    onChange(next)
    close({ returnFocus: true })
  }

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null)
      return
    }

    function placePanel() {
      const trigger = triggerRef.current
      if (!trigger) {
        return
      }
      const rect = trigger.getBoundingClientRect()
      const width = Math.min(PANEL_WIDTH, window.innerWidth - PANEL_GUTTER * 2)
      const panelHeight = panelRef.current?.offsetHeight ?? 500
      const spaceBelow = window.innerHeight - rect.bottom - PANEL_GUTTER
      const spaceAbove = rect.top - PANEL_GUTTER
      const placement =
        spaceBelow >= Math.min(panelHeight, 420) || spaceBelow >= spaceAbove ? "bottom" : "top"
      const maxHeight = Math.max(
        240,
        (placement === "bottom" ? spaceBelow : spaceAbove) - PANEL_GAP
      )
      const top =
        placement === "bottom"
          ? rect.bottom + PANEL_GAP
          : Math.max(PANEL_GUTTER, rect.top - Math.min(panelHeight, maxHeight) - PANEL_GAP)
      const left = Math.min(
        Math.max(PANEL_GUTTER, rect.right - width),
        window.innerWidth - width - PANEL_GUTTER
      )
      setPosition({ left, top, width, maxHeight, placement })
    }

    placePanel()
    window.addEventListener("resize", placePanel)
    window.addEventListener("scroll", placePanel, true)
    return () => {
      window.removeEventListener("resize", placePanel)
      window.removeEventListener("scroll", placePanel, true)
    }
  }, [open, mode, year])

  useEffect(() => {
    if (!open) {
      return
    }
    const focusTimer = window.setTimeout(() => {
      panelRef.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')?.focus()
    })
    return () => window.clearTimeout(focusTimer)
  }, [open])

  useEffect(() => {
    if (!open) {
      return
    }
    function onPointerDown(event: PointerEvent) {
      const target = event.target
      if (!(target instanceof Node)) {
        return
      }
      if (!triggerRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        close({ returnFocus: false })
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault()
        close({ returnFocus: true })
      }
    }
    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [close, open])

  function openPicker() {
    setMode(value.kind)
    setDraft(selectionToRange(value))
    if (value.kind === DASHBOARD_DATE_SELECTION_KIND.month) {
      setYear(Number(value.month.slice(0, 4)))
    } else {
      setYear(currentDate.getFullYear())
    }
    setOpen(true)
  }

  const panel = open ? (
    <PickerPanel
      panelRef={panelRef}
      pickerId={pickerId}
      position={position}
      mode={mode}
      value={value}
      currentDate={currentDate}
      earliestDate={earliestDate}
      earliestMonthDate={earliestMonthDate}
      availableRange={available}
      isBoundedRange={isBoundedRange}
      periods={periods}
      label={label}
      year={year}
      draft={draft}
      onModeChange={setMode}
      onYearChange={setYear}
      onDraftChange={setDraft}
      onCommit={commit}
      onCancel={() => close({ returnFocus: true })}
    />
  ) : null

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? close({ returnFocus: false }) : openPicker())}
        className="inline-flex h-10 max-w-full items-center gap-2 rounded-full border border-border bg-card px-3.5 text-[13px] font-medium text-foreground shadow-card outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring/40"
      >
        <CalendarDays className="size-4 shrink-0 text-muted-foreground" />

        <span className="truncate tabular-nums">
          {dashboardSelectionLabel(value, new Date(`${currentDay}T12:00:00.000Z`))}
        </span>

        <ChevronDown
          className={cn("size-3.5 shrink-0 transition-transform", open && "rotate-180")}
        />
      </button>

      {panel}
    </>
  )
}

type PickerPanelProps = {
  panelRef: RefObject<HTMLDivElement | null>
  pickerId: string
  position: FloatingPosition | null
  mode: PickerMode
  value: DashboardDateSelection
  currentDate: Date
  earliestDate: Date
  earliestMonthDate: Date
  availableRange: { from: string; to: string }
  isBoundedRange: boolean
  periods: readonly DashboardPeriod[]
  label: string
  year: number
  draft: DateRange | undefined
  onModeChange: (mode: PickerMode) => void
  onYearChange: (year: number) => void
  onDraftChange: (range: DateRange | undefined) => void
  onCommit: (selection: DashboardDateSelection) => void
  onCancel: () => void
}

function PickerPanel({
  panelRef,
  pickerId,
  position,
  mode,
  value,
  currentDate,
  earliestDate,
  earliestMonthDate,
  availableRange,
  isBoundedRange,
  periods,
  label,
  year,
  draft,
  onModeChange,
  onYearChange,
  onDraftChange,
  onCommit,
  onCancel,
}: PickerPanelProps) {
  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={`Choose ${label.toLowerCase()}`}
      data-placement={position?.placement}
      className="motion-floating-surface fixed z-50 overflow-y-auto rounded-xl border border-border bg-popover text-popover-foreground shadow-floating"
      style={floatingStyle(position)}
    >
      <PickerTabs pickerId={pickerId} mode={mode} onChange={onModeChange} />

      <PickerTabPanel
        pickerId={pickerId}
        mode={DASHBOARD_DATE_SELECTION_KIND.rolling}
        activeMode={mode}
      >
        <RollingPanel value={value} periods={periods} onChange={onCommit} />
      </PickerTabPanel>

      <PickerTabPanel
        pickerId={pickerId}
        mode={DASHBOARD_DATE_SELECTION_KIND.month}
        activeMode={mode}
      >
        <MonthPanel
          value={value}
          currentDate={currentDate}
          earliestDate={earliestMonthDate}
          availableRange={availableRange}
          isBoundedRange={isBoundedRange}
          year={year}
          onYearChange={onYearChange}
          onChange={onCommit}
        />
      </PickerTabPanel>

      <PickerTabPanel
        pickerId={pickerId}
        mode={DASHBOARD_DATE_SELECTION_KIND.custom}
        activeMode={mode}
      >
        <CustomPanel
          draft={draft}
          min={earliestDate}
          max={currentDate}
          onDraftChange={onDraftChange}
          onApply={(range) =>
            onCommit({
              kind: DASHBOARD_DATE_SELECTION_KIND.custom,
              from: localDateToDay(range.from),
              to: localDateToDay(range.to),
            })
          }
          onCancel={onCancel}
        />
      </PickerTabPanel>
    </div>,
    document.body
  )
}

function floatingStyle(position: FloatingPosition | null): CSSProperties {
  if (!position) {
    return {
      left: PANEL_GUTTER,
      top: PANEL_GUTTER,
      width: `calc(100vw - ${PANEL_GUTTER * 2}px)`,
      maxHeight: `calc(100vh - ${PANEL_GUTTER * 2}px)`,
      visibility: "hidden",
    }
  }
  return {
    left: position.left,
    top: position.top,
    width: position.width,
    maxHeight: position.maxHeight,
    visibility: "visible",
  }
}

function PickerTabs({
  pickerId,
  mode,
  onChange,
}: {
  pickerId: string
  mode: PickerMode
  onChange: (mode: PickerMode) => void
}) {
  const tabs: { value: PickerMode; label: string }[] = [
    { value: DASHBOARD_DATE_SELECTION_KIND.rolling, label: "Rolling" },
    { value: DASHBOARD_DATE_SELECTION_KIND.month, label: "Month" },
    { value: DASHBOARD_DATE_SELECTION_KIND.custom, label: "Custom" },
  ]
  return (
    <div
      className="grid grid-cols-3 gap-1 border-b border-border/70 bg-muted/35 p-1.5"
      role="tablist"
    >
      {tabs.map((tab, index) => (
        <button
          key={tab.value}
          id={tabId(pickerId, tab.value)}
          type="button"
          role="tab"
          aria-selected={mode === tab.value}
          aria-controls={panelId(pickerId, tab.value)}
          tabIndex={mode === tab.value ? 0 : -1}
          onClick={() => onChange(tab.value)}
          onKeyDown={(event) => {
            const nextIndex = nextTabIndex(event.key, index, tabs.length)
            if (nextIndex === null) {
              return
            }
            const nextTab = tabs[nextIndex]
            if (!nextTab) {
              return
            }
            event.preventDefault()
            onChange(nextTab.value)
            event.currentTarget.parentElement
              ?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
              .item(nextIndex)
              .focus()
          }}
          className={cn(
            "rounded-full px-3 py-2 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/40",
            mode === tab.value
              ? "bg-card text-foreground shadow-card ring-1 ring-border"
              : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

function PickerTabPanel({
  pickerId,
  mode,
  activeMode,
  children,
}: {
  pickerId: string
  mode: PickerMode
  activeMode: PickerMode
  children: ReactNode
}) {
  return (
    <div
      id={panelId(pickerId, mode)}
      role="tabpanel"
      aria-labelledby={tabId(pickerId, mode)}
      hidden={mode !== activeMode}
      tabIndex={-1}
    >
      {mode === activeMode ? children : null}
    </div>
  )
}

function nextTabIndex(key: string, currentIndex: number, tabCount: number): number | null {
  if (key === "Home") {
    return 0
  }
  if (key === "End") {
    return tabCount - 1
  }
  if (key === "ArrowRight") {
    return (currentIndex + 1) % tabCount
  }
  if (key === "ArrowLeft") {
    return (currentIndex - 1 + tabCount) % tabCount
  }
  return null
}

function tabId(pickerId: string, mode: PickerMode): string {
  return `${pickerId}-dashboard-date-${mode}-tab`
}

function panelId(pickerId: string, mode: PickerMode): string {
  return `${pickerId}-dashboard-date-${mode}-panel`
}

function RollingPanel({
  value,
  periods,
  onChange,
}: {
  value: DashboardDateSelection
  periods: readonly DashboardPeriod[]
  onChange: (selection: DashboardDateSelection) => void
}) {
  return (
    <div className="space-y-1 p-3">
      {periods.map((period) => {
        const selected =
          value.kind === DASHBOARD_DATE_SELECTION_KIND.rolling && value.period === period
        return (
          <button
            key={period}
            type="button"
            onClick={() => onChange({ kind: DASHBOARD_DATE_SELECTION_KIND.rolling, period })}
            className={cn(
              "flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring/40",
              selected && "bg-muted font-medium"
            )}
          >
            {DASHBOARD_PERIOD_LABELS[period]}

            {selected ? <Check className="size-4 text-info" /> : null}
          </button>
        )
      })}
    </div>
  )
}

function MonthPanel({
  value,
  currentDate,
  earliestDate,
  availableRange,
  isBoundedRange,
  year,
  onYearChange,
  onChange,
}: {
  value: DashboardDateSelection
  currentDate: Date
  earliestDate: Date
  availableRange: { from: string; to: string }
  isBoundedRange: boolean
  year: number
  onYearChange: (year: number) => void
  onChange: (selection: DashboardDateSelection) => void
}) {
  const minYear = earliestDate.getFullYear()
  const maxYear = currentDate.getFullYear()
  const earliestMonth = monthKey(earliestDate.getFullYear(), earliestDate.getMonth())
  const currentMonth = monthKey(currentDate.getFullYear(), currentDate.getMonth())
  return (
    <div className="p-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label="Previous year"
          disabled={year <= minYear}
          onClick={() => onYearChange(year - 1)}
          className="inline-flex size-8 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-30"
        >
          <ChevronLeft className="size-4" />
        </button>

        <span className="text-sm font-semibold tabular-nums">{year}</span>

        <button
          type="button"
          aria-label="Next year"
          disabled={year >= maxYear}
          onClick={() => onYearChange(year + 1)}
          className="inline-flex size-8 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-30"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        {MONTH_LABELS.map((label, monthIndex) => {
          const month = monthKey(year, monthIndex)
          const monthStart = `${month}-01`
          const monthEnd = lastDayOfMonth(month)
          const effectiveMonthEnd = month === currentMonth ? availableRange.to : monthEnd
          const unavailableInBoundedRange =
            isBoundedRange &&
            (monthStart < availableRange.from || effectiveMonthEnd > availableRange.to)
          const disabled =
            month < earliestMonth || month > currentMonth || unavailableInBoundedRange
          const selected =
            value.kind === DASHBOARD_DATE_SELECTION_KIND.month && value.month === month
          return (
            <button
              key={month}
              type="button"
              disabled={disabled}
              onClick={() => onChange({ kind: DASHBOARD_DATE_SELECTION_KIND.month, month })}
              className={cn(
                "rounded-lg px-3 py-3 text-[13px] outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:text-muted-foreground/25",
                selected && "bg-primary font-medium text-primary-foreground hover:bg-primary"
              )}
            >
              {label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function lastDayOfMonth(month: string): string {
  const [yearRaw, monthRaw] = month.split("-")
  const year = Number(yearRaw)
  const monthIndex = Number(monthRaw)
  return utcDay(Date.UTC(year, monthIndex, 0))
}

function CustomPanel({
  draft,
  min,
  max,
  onDraftChange,
  onApply,
  onCancel,
}: {
  draft: DateRange | undefined
  min: Date
  max: Date
  onDraftChange: (range: DateRange | undefined) => void
  onApply: (range: { from: Date; to: Date }) => void
  onCancel: () => void
}) {
  const isComplete = draft?.from !== undefined && draft.to !== undefined
  return (
    <div>
      <div className="border-b border-border/70 px-4 py-3 text-xs text-muted-foreground">
        <span className="tabular-nums">
          {draft?.from ? localDateToDay(draft.from) : "Start date"}
        </span>

        <span aria-hidden="true"> → </span>

        <span className="tabular-nums">{draft?.to ? localDateToDay(draft.to) : "End date"}</span>
      </div>

      <Calendar
        mode="range"
        required={false}
        resetOnSelect
        selected={draft}
        onSelect={onDraftChange}
        defaultMonth={draft?.to ?? max}
        startMonth={min}
        endMonth={max}
        disabled={[{ before: min }, { after: max }]}
        className="mx-auto"
      />

      <div className="flex justify-end gap-2 border-t border-border/70 px-4 py-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full px-3.5 py-2 text-xs font-medium text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          Cancel
        </button>

        <button
          type="button"
          disabled={!isComplete}
          onClick={() => {
            if (draft?.from && draft.to) {
              onApply({ from: draft.from, to: draft.to })
            }
          }}
          className="rounded-full bg-primary px-4 py-2 text-xs font-medium text-primary-foreground outline-none transition-colors hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-40"
        >
          Apply
        </button>
      </div>
    </div>
  )
}

function selectionToRange(selection: DashboardDateSelection): DateRange | undefined {
  if (selection.kind !== DASHBOARD_DATE_SELECTION_KIND.custom) {
    return undefined
  }
  return { from: dayToLocalDate(selection.from), to: dayToLocalDate(selection.to) }
}

function dayToLocalDate(day: string): Date {
  const [year, month, date] = day.split("-").map(Number)
  return new Date(year ?? 1970, (month ?? 1) - 1, date ?? 1, 12)
}

function localDateToDay(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`
}

function monthKey(year: number, monthIndex: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, "0")}`
}
